import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnDestroy } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DynamicField, Validation } from '@cargos/sprintpay-models';
import { CONFIG_APIS } from '@cargos/sprintpay-services';
import { IMaskModule } from 'angular-imask';
import { Subject, of, switchMap, take, takeUntil } from 'rxjs';
import { CustomerService } from 'src/app/shared/services';
import { FieldsDynamicFormService } from '../../services/fields.service';
import { DynamicErrorComponent } from '../dynamic-error/dynamic-error.component';

@Component({
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatTooltipModule,
        DynamicErrorComponent,
        IMaskModule,
    ],
    selector: 'app-dynamic-input',
    templateUrl: './dynamic-input.component.html',
    styleUrls: ['./dynamic-input.component.scss'],
})
export class DynamicInputComponent implements OnDestroy, AfterViewInit {
    private unsubscribe$ = new Subject<void>();
    private fieldWithValidationAPI: boolean = false;
    private paymentURL: string;
    public formName: FormGroup;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public maskOptions: any = null;

    @Input() field: DynamicField = {};

    constructor(
        @Inject(CONFIG_APIS) private readonly apis: Record<string, string>,
        private formGroupDirective: FormGroupDirective,
        private fieldsDynamicFormService: FieldsDynamicFormService,
        private customerService: CustomerService,
        private cdRef: ChangeDetectorRef
    ) {
        this.formName = formGroupDirective.control;
        this.paymentURL = this.apis['payments'];
    }

    get fileInput(): AbstractControl<string> | null {
        return this.formName.get(this.field?.name || '');
    }

    ngAfterViewInit(): void {
        this.fieldWithValidationAPI = !!this.hasAutoValidatorAPI();
        this.fetchDataIfIsProvider();
        this.fetchAutoValidator();
        this.setMask();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    fetchDataIfIsProvider(): void {
        if (!this.field.providerId) {
            return;
        }

        if (this.field.providerId) {
            this.fileInput?.valueChanges
                .pipe(
                    switchMap((value) => {
                        return this.fieldsDynamicFormService.fetchDataIfIsProvider(this.field, value).pipe(take(1));
                    }),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe();
        }
    }

    fetchAutoValidator(): void {
        if (!this.fieldWithValidationAPI) {
            return;
        }
        const validationWithAPI = this.hasAutoValidatorAPI();

        this.fileInput?.valueChanges
            .pipe(
                switchMap((value) => {
                    if (value && validationWithAPI) {
                        let values: string[];
                        if (!validationWithAPI.apiValidator && typeof validationWithAPI?.value === 'string') {
                            values = validationWithAPI.value.split('?');
                        } else {
                            values = validationWithAPI.apiValidator?.split('?') || [''];
                        }
                        const url = values[0];
                        const params = this.getParam(validationWithAPI, values[1], value);
                        const newValue = `${url}${params}`;
                        return this.fieldsDynamicFormService.fetchAutoValidator(newValue).pipe(take(1));
                    }

                    return of(null);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (isValid) => {
                    if (!isValid && validationWithAPI?.name) {
                        this.fileInput?.setErrors({ [validationWithAPI.name]: true });
                    }
                },
            });
    }

    getParam(validationWithAPI: Validation, data: string, value: string): string {
        let params = '';
        data.split('&').forEach((item, index) => {
            const aux = item.replace(/{|}/g, '');
            const newParam = this.getValueParam(validationWithAPI, aux, value);
            if (index === 0) {
                params = `?${aux}=${newParam}`;
            } else {
                params += `&${aux}=${newParam}`;
            }
        });
        return params;
    }

    getValueParam(validationWithAPI: Validation, param: string, value: string): string | number | null | undefined {
        switch (param) {
            case 'company':
                return this.customerService.getCompanyName();
                break;
            case 'validationId':
                return validationWithAPI.id;
                break;
            case 'term':
                return value;
                break;
            case 'name':
                return value;
                break;
            default:
                return '';
                break;
        }
    }

    hasAutoValidatorAPI(): Validation | undefined {
        return this.field.validations?.find(
            (validation) => validation.apiValidator || validation.name === 'apiValidatorData'
        );
    }

    setMask(): void {
        if (typeof this.field.mask === 'string' && this.isJsonString(this.field.mask)) {
            let mask = JSON.parse(this.field.mask);
            if (!mask.mask) {
                mask = '';
            }
            if (
                mask &&
                mask.blocks &&
                mask.blocks.num &&
                typeof mask.blocks.num.mask === 'string' &&
                mask.blocks.num.mask === 'Number'
            ) {
                mask.blocks.num.mask = Number;
            }
            this.maskOptions = mask;
        } else if (this.field.mask) {
            this.maskOptions = { mask: this.field.mask };
        } else {
            this.maskOptions = null;
        }
        this.cdRef.detectChanges();
    }

    private isJsonString(str: string): boolean {
        try {
            const obj = JSON.parse(str);
            return typeof obj === 'object' && obj !== null;
        } catch (e) {
            return false;
        }
    }
}
